import React from "react";
import { Avatar, Box, Stack } from "@mui/material";
import { useCachedAvatar } from "../../hooks/useCachedAvatar";

/**
 * CachedAvatar - useCachedAvatar를 사용하는 개별 아바타 컴포넌트
 */
const CachedAvatar = ({ user, size, index, overlap, totalVisible }) => {
  const userId = String(user?.id || user?.profile?.id || "");
  const username = user?.profile?.username || "";
  const fallbackUrl = user?.fallbackUrl || null;
  
  const { imageSrc } = useCachedAvatar(userId, fallbackUrl, size);

  return (
    <Avatar
      src={imageSrc || undefined}
      sx={{
        width: size,
        height: size,
        border: '2px solid',
        borderColor: 'background.paper',
        marginLeft: index > 0 ? `-${overlap}px` : 0,
        zIndex: totalVisible - index,
        position: 'relative',
      }}
    >
      {username ? username.charAt(0).toUpperCase() : "?"}
    </Avatar>
  );
};

/**
 * AvatarStack
 * 인스타 스타일로 아바타들을 겹쳐서 표시하는 컴포넌트 (useCachedAvatar 사용)
 * 
 * @param {object} props
 * @param {object[]} props.users - 유저 객체 배열 (id, profile, fallbackUrl 포함)
 * @param {number} [props.maxVisible] - 최대 표시 개수 (기본값: 4)
 * @param {number} [props.size] - 아바타 크기 (기본값: 32)
 * @param {number} [props.overlap] - 겹침 정도 (기본값: 8)
 */
const AvatarStack = ({ 
  users = [], 
  maxVisible = 4, 
  size = 32, 
  overlap = 8 
}) => {
  if (!users || users.length === 0) return null;

  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = Math.max(0, users.length - maxVisible);

  return (
    <Stack direction="row" spacing={0} sx={{ position: 'relative' }}>
      {visibleUsers.map((user, index) => (
        <CachedAvatar
          key={user?.id || index}
          user={user}
          size={size}
          index={index}
          overlap={overlap}
          totalVisible={visibleUsers.length}
        />
      ))}
      {remainingCount > 0 && (
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: 'grey.300',
            border: '2px solid',
            borderColor: 'background.paper',
            marginLeft: `-${overlap}px`,
            zIndex: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: size / 3,
            fontWeight: 'bold',
            color: 'text.secondary',
            position: 'relative',
          }}
        >
          +{remainingCount}
        </Box>
      )}
    </Stack>
  );
};

export default AvatarStack;